import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { STRIPE_SERVER } from '~/config/global.config';
import { useAuth } from '~/services/auth';
import { adsAllowed, getAttribution, getFbcCookie, getFbpCookie, trackMetaPixel } from '~/utils/attribution';
import { getSignupFlow, parseSignupFlow } from '~/utils/signupFlow';
import { saveSignupLang } from '~/utils/signupLang';
import { annualPriceLabel, getTrialPrice, proPriceLabel } from '~/utils/trialPricing';
import { isReferralCode, normalizeReferralCode, referralCodeFromLink, REFERRAL_TRIAL_DAYS } from '~/utils/referral';
import { lookupReferralCode } from '~/services/referral/referralCode';

import { AuthTimeline } from './components/AuthTimeline';
import { TrialTimeline } from './components/TrialTimeline';
import { errorMessage } from './utils';
import { AuthShell, AuthField, AuthLink, AuthSelect, AuthSubmit } from './AuthKit';
import styles from './auth.redesign.module.css';

/**
 * Onboarding "Infos" step (after sign up): company info + plan → Stripe checkout.
 * Formule : Standard ou Pro (« Ma marque »), au mois ou à l'année → champ `plan` envoyé à
 * stripe-b2b (`monthly`, `annual`, `pro`, `pro_annual`), qui choisit le prix.
 */
type Tier = 'standard' | 'pro';
type Period = 'month' | 'year';
const PLAN_OF: Record<Tier, Record<Period, string>> = {
    standard: { month: 'monthly', year: 'annual' },
    pro: { month: 'pro', year: 'pro_annual' },
};

/** Formule demandée par le site (`?plan=annual|pro|pro_annual`, `?plan=annual&from=pro`) ou mémorisée. */
function initialPlan(params: URLSearchParams, stored: string | null): { tier: Tier; period: Period } {
    const asked = params.get('plan');
    let plan = asked || stored || 'monthly';
    if (asked === 'annual' && params.get('from') === 'pro') plan = 'pro_annual';
    return {
        tier: plan === 'pro' || plan === 'pro_annual' ? 'pro' : 'standard',
        period: plan === 'annual' || plan === 'pro_annual' ? 'year' : 'month',
    };
}
function Infos({ children, userFlow }: { children?: React.ReactNode; userFlow?: string }) {
    const { t, i18n } = useTranslation();

    const { auth, loading, resetError, emailErrorCode } = useAuth();

    const [coupon, setCoupon] = useState('');
    // Formule : Standard au mois par défaut ; sinon choisie ici ou sur le site (?plan=…).
    const [tier, setTier] = useState<Tier>('standard');
    const [period, setPeriod] = useState<Period>('month');
    const [attribution, setAttribution] = useState<Record<string, string>>({});
    // Test A/B du parcours (utils/signupFlow) : version du profil (tirée à /signup), sinon celle
    // du navigateur, sinon dérivée du compte.
    const savedFlow = parseSignupFlow(userFlow);
    const [flow] = useState(() => getSignupFlow({ uid: auth?.uid, saved: savedFlow }));
    // Compteurs GA4 par version : seulement les comptes nés pendant le test (version dans le
    // profil). Les anciens comptes ramenés par la relance diluaient les taux.
    // (et pas un `?flow=` de test qui contredirait le profil).
    const countFlow = savedFlow !== undefined && flow === savedFlow;
    const v2 = flow === 'v2';
    // v2 : langue sur une ligne (sélecteur à la demande), « comment connu » facultatif, code promo replié.
    const [showLang, setShowLang] = useState(false);
    const [howMeet, setHowMeet] = useState('');
    const [showCoupon, setShowCoupon] = useState(false);
    // Parrainage : code apporté par le lien (prérempli) ou tapé (autre appareil). Vérifié en lisant
    // `referralCodes/{CODE}` (nom du lieu parrain) ; stripe-b2b redécide à l'envoi.
    const [referralCode, setReferralCode] = useState('');
    const [showReferral, setShowReferral] = useState(false);
    const [referralCheck, setReferralCheck] = useState<{
        code: string;
        status: 'valid' | 'unknown' | 'unverified';
        venueName: string | null;
    } | null>(null);

    useEffect(() => {
        logEvent('infos_view', 'Infos', { flow });
        if (countFlow) logEvent(`infos_view_${flow}`, 'Infos');
        document.title = 'Infos - Unboared';

        // UTM + fbclid capturés au bootstrap (main.tsx), envoyés au checkout
        // Stripe en champs cachés. Le cookie _fbc (posé par le pixel) porte le
        // vrai timestamp du clic d'ad — prioritaire pour la Conversions API.
        // Le cookie _fbp (navigateur) rattache l'essai serveur aux visites du pixel.
        // Publicité refusée dans le bandeau (cookie `ub_consent` a0) : aucun identifiant Meta ne part
        // vers le serveur (Conversions API), comme le pixel côté navigateur.
        const adsOk = adsAllowed();
        const fbc = adsOk ? getFbcCookie() : undefined;
        const fbp = adsOk ? getFbpCookie() : undefined;
        const acquisition = getAttribution();
        if (!adsOk) delete acquisition.fbclid;
        setAttribution({ ...acquisition, ...(fbc ? { fbc } : {}), ...(fbp ? { fbp } : {}) });
        const linkCode = referralCodeFromLink();
        if (linkCode) {
            setReferralCode(linkCode);
            setShowReferral(true);
        }

        // Pre-fill coupon from URL or from localStorage (saved during signup)
        const params = new URLSearchParams(window.location.search);
        const urlCoupon = params.get('coupon');
        const savedCoupon = localStorage.getItem('signup_coupon');
        if (urlCoupon) {
            setCoupon(urlCoupon.toUpperCase());
            setShowCoupon(true);
        } else if (savedCoupon) {
            setCoupon(savedCoupon.toUpperCase());
            setShowCoupon(true);
        }
        let storedPlan: string | null = null;
        try {
            storedPlan = localStorage.getItem('signup_plan');
        } catch {
            storedPlan = null;
        }
        const start = initialPlan(params, storedPlan);
        setTier(start.tier);
        setPeriod(start.period);
    }, []);

    // Les codes partenaires sont des tarifs Standard au mois : avec un code, la formule reste
    // Standard au mois (stripe-b2b applique la même règle).
    const annualAvailable = coupon.trim() === '';
    const effectiveTier: Tier = annualAvailable ? tier : 'standard';
    const effectivePeriod: Period = annualAvailable ? period : 'month';
    const effectivePlan = PLAN_OF[effectiveTier][effectivePeriod];

    // Le dernier choix fait foi (retour depuis Stripe : l'étape Infos se remonte).
    const remember = (next: { tier: Tier; period: Period }) => {
        setTier(next.tier);
        setPeriod(next.period);
        try {
            localStorage.setItem('signup_plan', PLAN_OF[next.tier][next.period]);
        } catch {
            /* stockage indisponible : le choix vaut pour cette page */
        }
    };
    const chooseTier = (next: Tier) => remember({ tier: next, period });
    const choosePeriod = (next: Period) => remember({ tier, period: next });

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        // Relit les cookies du pixel au clic : au chargement de la page, _fbp
        // peut ne pas encore exister (pixel pas fini de charger).
        const form = event.currentTarget;
        (
            [
                ['fbc', adsAllowed() ? getFbcCookie() : undefined],
                ['fbp', adsAllowed() ? getFbpCookie() : undefined],
            ] as const
        ).forEach(([name, value]) => {
            if (!value) return;
            let input = form.elements.namedItem(name) as HTMLInputElement | null;
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = name;
                form.appendChild(input);
            }
            input.value = value;
        });
        logEvent('infos_click', 'Infos', { plan: effectivePlan, flow });
        if (countFlow) logEvent(`infos_click_${flow}`, 'Infos');
        trackMetaPixel('EssaiLance', { custom: true });
    };

    const lang = i18n.language.split('-')[0];
    // Langues proposées à l'inscription ; une autre langue de navigateur retombe sur l'anglais
    // (comme le sélecteur, qui affiche alors sa 1re option).
    const langValue = ['en', 'fr', 'es', 'de'].includes(lang) ? lang : 'en';
    const langOptions = [
        { value: 'en', label: t('global.infos.input.langOptions.en') },
        { value: 'fr', label: t('global.infos.input.langOptions.fr') },
        { value: 'es', label: t('global.infos.input.langOptions.es') },
        { value: 'de', label: t('global.infos.input.langOptions.de') },
    ];
    const howMeetOptions = [
        { value: 'event', label: t('global.infos.input.howMeetOptions.event') },
        { value: 'bar', label: t('global.infos.input.howMeetOptions.bar') },
        { value: 'social_networks', label: t('global.infos.input.howMeetOptions.social_networks') },
        { value: 'word_of_mouth', label: t('global.infos.input.howMeetOptions.word_of_mouth') },
        { value: 'browser', label: t('global.infos.input.howMeetOptions.browser') },
        { value: 'chatgpt', label: t('global.infos.input.howMeetOptions.chatgpt') },
        { value: 'other', label: t('global.infos.input.howMeetOptions.other') },
    ];
    // Le lien disparaît au clic : on place le curseur sur le champ qui le remplace.
    const reveal = (show: (v: boolean) => void, fieldId: string) => {
        show(true);
        setTimeout(() => document.getElementById(fieldId)?.focus(), 0);
    };
    const onLangChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        // Bascule l'interface immédiatement et persiste le choix pour la page success (post-Stripe).
        i18n.changeLanguage(e.target.value);
        saveSignupLang(e.target.value);
    };

    useEffect(() => {
        const code = normalizeReferralCode(referralCode);
        if (!isReferralCode(code)) {
            setReferralCheck(null);
            return undefined;
        }
        let alive = true;
        const timer = window.setTimeout(() => {
            lookupReferralCode(code).then((found) => {
                if (!alive) return;
                if (found === undefined) setReferralCheck({ code, status: 'unverified', venueName: null });
                else if (found === null) setReferralCheck({ code, status: 'unknown', venueName: null });
                else {
                    setReferralCheck({ code, status: 'valid', venueName: found.venueName });
                    logEvent('signup_referral_code_valid', 'Infos', { code });
                }
            });
        }, 350);
        return () => {
            alive = false;
            window.clearTimeout(timer);
        };
    }, [referralCode]);

    // Parrainage confirmé (code existant) et pas de code partenaire : 1 mois d'essai annoncé partout.
    const referred = !coupon && referralCheck?.status === 'valid';
    const referralVenue = referred ? referralCheck?.venueName : null;

    // Frise de l'essai : prix estimé selon la région (Stripe tranche au checkout),
    // sauf coupons partenaires français à prix fixe en euros.
    const renderPlan = () => {
        if (coupon === 'CINEO2026' || coupon === 'CINEO') {
            return <TrialTimeline priceLabel={'39\u00a0€'} strikeLabel={'49\u00a0€'} tag="Offre partenaire CINEO" />;
        }
        if (coupon === 'VANDB') {
            return <TrialTimeline priceLabel={'45\u00a0€'} strikeLabel={'49\u00a0€'} tag="Offre réseau V&B" />;
        }
        // Parrainage (sans code partenaire) : 1 mois (30 jours) d'essai, accordé par stripe-b2b.
        const trialDays = referred ? REFERRAL_TRIAL_DAYS : undefined;
        const cur = getTrialPrice().currency;
        if (effectiveTier === 'pro') {
            return (
                <TrialTimeline
                    priceLabel={proPriceLabel(effectivePeriod, cur)}
                    period={effectivePeriod}
                    trialDays={trialDays}
                />
            );
        }
        if (effectivePeriod === 'year') {
            return <TrialTimeline priceLabel={annualPriceLabel(cur)} period="year" trialDays={trialDays} />;
        }
        return <TrialTimeline priceLabel={getTrialPrice().label} trialDays={trialDays} />;
    };

    const referralBanner = referred ? (
        <p className={styles.referralBanner} role="status">
            <strong>
                {referralVenue
                    ? t('global.infos.referral.titleFrom', { venue: referralVenue })
                    : t('global.infos.referral.title')}
            </strong>{' '}
            {t('global.infos.referral.body')}
        </p>
    ) : null;

    let referralHelper: string | undefined;
    if (coupon && referralCheck?.status === 'valid') referralHelper = t('global.infos.referral.notWithCoupon');
    else if (referralCheck?.status === 'unknown') referralHelper = t('global.infos.referral.unknown');

    const referralField = showReferral ? (
        <AuthField
            id="referralCode"
            label={t('global.infos.referral.codeLabel')}
            placeholder={t('global.infos.referral.codePlaceholder')}
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            error={referralCheck?.status === 'unknown'}
            helper={referralHelper}
        />
    ) : (
        <p className={styles.inlineRow}>
            <AuthLink onClick={() => setShowReferral(true)}>{t('global.infos.referral.toggle')}</AuthLink>
        </p>
    );

    const { currency, label: monthlyLabel } = getTrialPrice();

    const company = (labelKey: string, placeholderKey: string) => (
        <AuthField
            id="companyName"
            label={t(labelKey)}
            placeholder={t(placeholderKey)}
            onSelect={resetError}
            error={emailErrorCode !== undefined}
            helper={emailErrorCode !== undefined ? t(`global.error.${errorMessage(emailErrorCode)}`) : undefined}
            required
        />
    );

    const couponField = (
        <AuthField
            id="coupon"
            label={t('global.infos.trial.couponLabel')}
            placeholder={t('global.infos.trial.couponPlaceholder')}
            value={coupon}
            onChange={(e) => setCoupon(e.target.value.toUpperCase())}
        />
    );

    const priceOf = (t2: Tier, p: Period) => {
        if (t2 === 'pro') return proPriceLabel(p, currency);
        return p === 'year' ? annualPriceLabel(currency) : monthlyLabel;
    };
    const pricePerKey = (p: Period) =>
        p === 'year' ? 'global.infos.plan.annualPrice' : 'global.infos.plan.monthlyPrice';
    const option = (args: {
        group: string;
        value: string;
        selected: boolean;
        disabled?: boolean;
        onChange: () => void;
        name: string;
        price: string;
        desc: string;
        badge?: string;
    }) => (
        <label
            className={`${styles.planOption} ${args.selected ? styles.planOptionSel : ''} ${
                args.disabled ? styles.planOptionOff : ''
            }`}
        >
            <input
                type="radio"
                name={args.group}
                value={args.value}
                checked={args.selected}
                disabled={args.disabled}
                aria-describedby={args.disabled ? 'plan-coupon-note' : undefined}
                onChange={args.onChange}
            />
            {args.badge && <span className={styles.planBadge}>{args.badge}</span>}
            <span className={styles.planName}>{args.name}</span>
            <span className={styles.planPrice}>{args.price}</span>
            <span className={styles.planDesc}>{args.desc}</span>
        </label>
    );

    const planBlock = (
        <>
            <span className={styles.planLabel}>{t('global.infos.plan.title')}</span>
            <div className={styles.planChoice} role="radiogroup" aria-label={t('global.infos.plan.title')}>
                {option({
                    group: 'tierChoice',
                    value: 'standard',
                    selected: effectiveTier === 'standard',
                    onChange: () => chooseTier('standard'),
                    name: t('global.infos.plan.standard'),
                    price: t(pricePerKey(effectivePeriod), { price: priceOf('standard', effectivePeriod) }),
                    desc: t('global.infos.plan.standardDesc'),
                })}
                {option({
                    group: 'tierChoice',
                    value: 'pro',
                    selected: effectiveTier === 'pro',
                    disabled: !annualAvailable,
                    onChange: () => chooseTier('pro'),
                    name: t('global.infos.plan.pro'),
                    price: t(pricePerKey(effectivePeriod), { price: priceOf('pro', effectivePeriod) }),
                    desc: t('global.infos.plan.proDesc'),
                })}
            </div>
            <span className={styles.planLabel}>{t('global.infos.plan.periodTitle')}</span>
            <div className={styles.planChoice} role="radiogroup" aria-label={t('global.infos.plan.periodTitle')}>
                {option({
                    group: 'planChoice',
                    value: 'monthly',
                    selected: effectivePeriod === 'month',
                    onChange: () => choosePeriod('month'),
                    name: t('global.infos.plan.monthly'),
                    price: t('global.infos.plan.monthlyPrice', { price: priceOf(effectiveTier, 'month') }),
                    desc: t('global.infos.plan.monthlyDesc'),
                })}
                {option({
                    group: 'planChoice',
                    value: 'annual',
                    selected: effectivePeriod === 'year',
                    disabled: !annualAvailable,
                    onChange: () => choosePeriod('year'),
                    name: t('global.infos.plan.annual'),
                    price: t('global.infos.plan.annualPrice', { price: priceOf(effectiveTier, 'year') }),
                    desc: t('global.infos.plan.annualDesc'),
                    badge: t('global.infos.plan.badge'),
                })}
            </div>
            {!annualAvailable && (
                <p id="plan-coupon-note" className={styles.planNote}>
                    {t('global.infos.plan.couponNote')}
                </p>
            )}
            <input type="hidden" name="plan" value={effectivePlan} />
        </>
    );

    const hidden = (
        <>
            <input type="hidden" name="userID" value={auth.uid} />
            {Object.entries(attribution).map(([key, value]) => (
                <input key={key} type="hidden" name={key} value={value} />
            ))}
        </>
    );

    if (v2) {
        return (
            <>
                <AuthShell>
                    <form onSubmit={handleSubmit} action={`${STRIPE_SERVER}/create-checkout-session`} method="POST">
                        <AuthTimeline currentStep={1} trialLabel />
                        {referralBanner}
                        <h2 className={styles.title}>
                            {t(referred ? 'global.infos.v2.titleReferral' : 'global.infos.v2.title')}
                        </h2>
                        <p className={styles.subtitle}>{t('global.infos.v2.subtitle')}</p>

                        {company('global.infos.v2.companyName', 'global.infos.v2.companyNamePlaceholder')}

                        {showLang ? (
                            <AuthSelect
                                id="lang"
                                label={t('global.infos.input.lang')}
                                defaultValue={langValue}
                                required
                                onChange={onLangChange}
                                options={langOptions}
                            />
                        ) : (
                            <p className={styles.inlineRow}>
                                <span>
                                    {t('global.infos.v2.langLine', { lang: '' })}
                                    <strong>{t(`global.infos.input.langOptions.${langValue}`)}</strong>
                                </span>
                                <AuthLink onClick={() => reveal(setShowLang, 'lang')}>
                                    {t('global.infos.v2.changeLang')}
                                </AuthLink>
                                <input type="hidden" name="lang" value={langValue} />
                            </p>
                        )}

                        {/* Facultatif : le sélecteur n'est pas envoyé (name vide), la valeur part par le
                            champ caché (« skipped » sans réponse : le webhook recopie howMeet dans
                            Firestore, qui refuse une valeur absente). */}
                        <AuthSelect
                            id="howMeetChoice"
                            name=""
                            label={t('global.infos.v2.howMeetOptional')}
                            placeholder={t('global.infos.input.howMeetPlaceholder')}
                            onChange={(e) => setHowMeet(e.target.value)}
                            options={howMeetOptions}
                        />
                        <input type="hidden" name="howMeet" value={howMeet || 'skipped'} />

                        {referralField}

                        {showCoupon ? (
                            couponField
                        ) : (
                            <p className={styles.inlineRow}>
                                <AuthLink onClick={() => reveal(setShowCoupon, 'coupon')}>
                                    {t('global.infos.v2.couponToggle')}
                                </AuthLink>
                            </p>
                        )}

                        {planBlock}
                        {hidden}

                        <AuthSubmit loading={loading}>
                            {t(referred ? 'home.demo.startTrialReferral' : 'home.demo.startTrial')}
                        </AuthSubmit>
                        <p className={styles.foot}>{t('global.infos.trial.nothingToday')}</p>

                        <span className={styles.planLabel} style={{ marginTop: 24 }}>
                            {t('global.infos.trial.title')}
                        </span>
                        {renderPlan()}
                    </form>
                </AuthShell>
                {children}
            </>
        );
    }

    return (
        <>
            <AuthShell>
                <form onSubmit={handleSubmit} action={`${STRIPE_SERVER}/create-checkout-session`} method="POST">
                    <AuthTimeline currentStep={1} />
                    {referralBanner}

                    {company('global.infos.input.companyName', 'global.infos.input.companyNamePlaceholder')}

                    <div className={styles.grid2}>
                        <AuthSelect
                            id="lang"
                            label={t('global.infos.input.lang')}
                            defaultValue={lang}
                            required
                            onChange={onLangChange}
                            options={langOptions}
                        />
                        <AuthSelect
                            id="howMeet"
                            label={t('global.infos.input.howMeet')}
                            placeholder={t('global.infos.input.howMeetPlaceholder')}
                            required
                            options={howMeetOptions}
                        />
                    </div>

                    {referralField}

                    {couponField}

                    {planBlock}

                    <span className={styles.planLabel}>{t('global.infos.trial.title')}</span>
                    {renderPlan()}

                    {hidden}

                    <AuthSubmit loading={loading}>
                        {t(referred ? 'home.demo.startTrialReferral' : 'home.demo.startTrial')}
                    </AuthSubmit>
                    <p className={styles.foot}>{t('global.infos.trial.nothingToday')}</p>
                </form>
            </AuthShell>
            {children}
        </>
    );
}

export default Infos;
